<template>
  <div class="url-input">
    <div class="url-input__rows">
      <div v-for="(item, index) in items" :key="index" class="url-input__row">
        <span v-if="showIndex" class="url-input__index">{{ String(index + 1).padStart(2, '0') }}</span>

        <div class="url-input__field-wrap">
          <el-input
            :value="item.value"
            :placeholder="placeholder"
            :disabled="!item.editable"
            :class="{ 'is-warning': Boolean(item.warning), 'is-error': Boolean(item.error) }"
            class="url-input__field"
            @input="onUpdate(index, $event)"
          >
            <i v-if="item.warning" slot="suffix" class="mdi mdi-alert url-input__warning-icon" />
          </el-input>

          <div v-if="item.error" class="input-invalid-text">{{ item.error }}</div>
          <div v-else-if="item.warning" class="input-warning-text">{{ item.warning }}</div>
        </div>

        <button
          v-if="canShowDelete"
          type="button"
          class="url-input__delete"
          :disabled="deleteDisabledList[index]"
          :aria-label="'URL を削除'"
          @click="onDelete(index)"
        >
          <Icon name="close" size="small" />
        </button>
      </div>
    </div>

    <el-button class="url-input__add-button" type="default" :disabled="!canAdd" @click="onAdd">
      <Icon name="plus" space="right" />
      URLを追加
    </el-button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'

import Icon from '@/client/components/_atoms/Icon.vue'

import { UrlInputItem } from './types'
import { stripUrlParams } from './url_input.utils'

export default defineComponent({
  name: 'UrlInput',
  components: {
    Icon
  },
  props: {
    items: {
      type: Array as PropType<UrlInputItem[]>,
      default: () => []
    },
    // 「+ URL を追加」ボタンの表示可否（最終集計中は false）。
    canAdd: {
      type: Boolean,
      default: true
    },
    // 削除（×）の可否（最終集計中は false）。行が 1 つかつ URL 未入力のときは可否に関わらず表示しない。
    canDelete: {
      type: Boolean,
      default: true
    },
    // 複数行時に連番を表示するか。
    showIndex: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'https://x.com/アカウント/status/...'
    }
  },
  setup(props, context) {
    // 行が 2 つ以上あるときは未入力の行も含めて × を表示する。
    // 1 行だけのときは、URL が入力済みのときのみ表示する（未入力の入力欄に × を出さない）。
    const canShowDelete = computed(
      () => props.items.length > 1 || props.items.some(item => item.value.trim().length > 0)
    )

    // 行ごとの × の disabled
    const deleteDisabledList = computed(() => props.items.map(item => !props.canDelete || item.deletable === false))

    // 値の更新は親が管理する。副作用（API・state 更新）は持たない。
    // クエリパラメータ・フラグメントは入力時点で切り落とし、親には切り落とし後の値だけを渡す。
    function onUpdate(index: number, value: string) {
      context.emit('update', { index, value: stripUrlParams(value) })
    }

    function onDelete(index: number) {
      context.emit('delete', index)
    }

    function onAdd() {
      context.emit('add')
    }

    return {
      canShowDelete,
      deleteDisabledList,
      onUpdate,
      onDelete,
      onAdd
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.url-input {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
  width: 100%;
}

.url-input__rows {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
}

.url-input__row {
  display: flex;
  gap: $spacing-unit;
  align-items: flex-start;
}

.url-input__index {
  flex-shrink: 0;
  padding-top: $spacing-unit;
  font-size: 14px;
  color: $sub-text-color;
}

.url-input__field-wrap {
  flex: 1;
}

.url-input__field {
  width: 100%;

  &.is-error {
    :deep(.el-input__inner) {
      border-color: $danger-color !important;
    }
  }

  &.is-warning {
    :deep(.el-input__inner) {
      border-color: $warning-color !important;
    }
  }

  // suffix アイコンを input 内で縦中央に揃える（警告文で高さが伸びても上寄りにならないように）
  :deep(.el-input__suffix) {
    display: flex;
    align-items: center;
  }
}

.url-input__warning-icon {
  color: $warning-color;
}

.url-input__delete {
  display: inline-flex;
  align-items: center;
  height: $spacing-unit * 5;
  padding: 0;
  cursor: pointer;
  background: none;
  border: none;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &:not(:disabled):hover {
    :deep(.mdi) {
      color: $danger-color;
    }
  }
}

.url-input__add-button {
  align-self: flex-start;
}

.input-invalid-text {
  margin-top: math.div($spacing-unit, 2);
  font-size: 14px;
  color: $danger-color;
}

.input-warning-text {
  margin-top: math.div($spacing-unit, 2);
  font-size: 14px;
  color: $warning-color;
}
</style>
